import { useCallback } from "react";
import { AsyncPanel } from "../components/AsyncPanel";
import { Card } from "../components/Card";
import { Container } from "../components/Container";
import { DeskNav } from "../components/DeskNav";
import { SectionHeading } from "../components/SectionHeading";
import { StatusPill } from "../components/StatusPill";
import type { StatusTone } from "../components/StatusPill";
import { AuctionPanel, AuctionSkeleton } from "../sections/AuctionPanel";
import { BidsPanel, BidsSkeleton } from "../sections/BidsPanel";
import { CreditPanel, CreditSkeleton } from "../sections/CreditPanel";
import { DealsPanel, DealsSkeleton } from "../sections/DealsPanel";
import { HistoryPanel, HistorySkeleton } from "../sections/HistoryPanel";
import { MentionsPanel, MentionsSkeleton } from "../sections/MentionsPanel";
import {
  fetchSellerAuction,
  placeSellerBid,
  purchaseHonorableMention,
} from "../api/auction";
import { fetchSellerBids } from "../api/bids";
import { fetchSellerDealDetail, saveSellerDeal } from "../api/deal-edit";
import { fetchSellerCredit, fetchSellerDeals } from "../api/desk";
import { fetchSellerPlacements } from "../api/placements";
import { fetchSubmissionTerms, submitSellerDeal } from "../api/submission";
import { useApiResource } from "../hooks/useApiResource";
import { useDeskView } from "../desk/useDeskView";
import { useSession } from "../session/useSession";
import { formatDisplayDateShort } from "../lib/format";
import styles from "./screens.module.css";
import desk from "./DeskScreen.module.css";
import type {
  DealEditPatch,
  PlacedBid,
  PurchasedMentionResult,
  SavedDeal,
  SellerAuctionView,
  SellerBidHistory,
  SellerCredit,
  SellerDeal,
  SellerDealDetail,
  SellerPlacementHistory,
  SubmissionTerms,
  SubmittedDeal,
  DealSubmission,
} from "../api/types";
import type { Seller } from "../session/seller";

/**
 * The signed-in surface (canon `APP-002`, rearranged by `APP-016`).
 *
 * Everything on this page came from a session-scoped read of `dealbid-core`: identity from
 * `GET /v1/seller/me`, deals from `GET /v1/seller/deals`, the balance from
 * `GET /v1/seller/credit`. None of those requests carries a seller id — the session decides
 * whose data comes back — so there is no value in this browser a caller could change to
 * reach another founder's desk (`docs/ARCHITECTURE.md` § Seller app).
 *
 * ## Four views, five reads
 *
 * This screen used to be seven full-width sections stacked in one column, reachable only by
 * scrolling. `contracts/design/app-desk/` turns them into four views — deals, tomorrow's
 * auction, results, and how a Dealbid day works — with `Submit a deal` lifted out of the
 * sixth section into a bar a founder can always reach.
 *
 * **The five reads are still five reads, and they still live here rather than in the views.**
 * That is deliberate and it is the part a rearrangement could most easily break:
 *
 * - a panel that failed keeps its error when a founder looks at another view and comes back,
 *   because nothing unmounted it;
 * - the auction poll runs once for the desk rather than restarting on every tab click;
 * - the credit balance is one read rendered in two places (the deals view's rail and the
 *   auction view's), so the desk cannot state two balances;
 * - and `docs/TESTING.md`'s four states stay four states *per panel* — a founder whose credit
 *   read fails still sees whether their deal was approved, which was true before this task
 *   and had to survive it.
 *
 * ## Where each view's content came from
 *
 * | View | Was |
 * |---|---|
 * | Your deals | § 6, plus credit and the auction's state in the rail |
 * | Tomorrow's auction | § 1 + § 2 + § 3 — the bid form, the mention, the balance beside them |
 * | Results | § 4 + § 5 — placements, then every bid |
 * | How it works | § 7 |
 *
 * Canon § 5 reserves credit while a bid stands, consumes it on a win and returns it on a
 * loss, which is why the balance moved next to the bid form: the number and the control that
 * changes it are read together rather than four sections apart.
 */

export interface DeskDependencies {
  /** Test seams. The shipped app always reads dealbid-core. */
  loadDeals?: (() => Promise<SellerDeal[]>) | undefined;
  loadCredit?: (() => Promise<SellerCredit>) | undefined;
  loadAuction?: (() => Promise<SellerAuctionView>) | undefined;
  loadPlacements?: (() => Promise<SellerPlacementHistory>) | undefined;
  loadBids?: (() => Promise<SellerBidHistory>) | undefined;
  placeBid?:
    | ((input: { dealId: string; amountCents: number }) => Promise<PlacedBid>)
    | undefined;
  purchaseMention?:
    | ((input: { dealId: string }) => Promise<PurchasedMentionResult>)
    | undefined;
  loadDealDetail?: ((dealId: string) => Promise<SellerDealDetail>) | undefined;
  saveDeal?:
    | ((input: { dealId: string; patch: DealEditPatch }) => Promise<SavedDeal>)
    | undefined;
  loadTerms?: (() => Promise<SubmissionTerms>) | undefined;
  submitDeal?: ((submission: DealSubmission) => Promise<SubmittedDeal>) | undefined;
}

/**
 * Canon `AGENTS.md` § 5 allows polling for auction UI in V0 and rules out WebSockets. One
 * minute is frequent enough that a rank goes stale by a minute rather than by a session,
 * and slow enough that a desk left open all day is not a load on core.
 */
const AUCTION_REFRESH_MS = 60_000;

/**
 * Presentation only. Core settles the seller status vocabulary in CORE-006; anything
 * unrecognised is shown neutrally, with the word itself always visible, so an unknown
 * status can never be silently coloured as if it were understood.
 */
function toneFor(status: string): StatusTone {
  const normalized = status.toLowerCase();
  if (normalized === "active") return "positive";
  if (normalized === "disabled" || normalized === "suspended") return "warning";
  return "neutral";
}

/**
 * How a Dealbid day works, in the wording `contracts/design/app-desk/` draws.
 *
 * These are not the three cards this screen carried before. The design record's refusal R5
 * explains why they changed rather than moved: the canvas annotates the tab "wording intact"
 * and it is not, but the wording it draws is the better of the two — the third card states
 * canon § 11's separation between bought placement and earned ranking, which the old cards
 * never said anywhere on this desk.
 */
const AUCTION_RULES = [
  {
    mark: "01",
    title: "Founders bid",
    body:
      "Each day founders bid for tomorrow's ten positions, and the ten highest qualifying bids win placement. A bid placed today never changes today's board — that one is already decided.",
  },
  {
    mark: "02",
    title: "The board resets at 00:00 UTC",
    body:
      "Winning deals go live for exactly one display day, ranked by winning bid and labelled as sponsored placement. Each day's auction is independent and opens again at the $1 minimum.",
  },
  {
    mark: "03",
    title: "Buyers decide the other lists",
    body:
      "The weekly and monthly lists are ranked by qualified buyer clicks, not by what anyone bid. Bidding cannot reach them.",
  },
];

export function DeskScreen({
  seller,
  loadDeals = fetchSellerDeals,
  loadCredit = fetchSellerCredit,
  loadAuction = fetchSellerAuction,
  loadPlacements = fetchSellerPlacements,
  loadBids = fetchSellerBids,
  placeBid = placeSellerBid,
  purchaseMention = purchaseHonorableMention,
  loadDealDetail = fetchSellerDealDetail,
  saveDeal = saveSellerDeal,
  loadTerms = fetchSubmissionTerms,
  submitDeal = submitSellerDeal,
}: { seller: Seller } & DeskDependencies) {
  // `retry` re-runs sign-in resolution. On an expired session it finds none and lands on
  // the sign-in screen, which is the correct destination for a `401` from any panel.
  const { retry } = useSession();

  /*
   * Which view is showing, and whether the submission form is open.
   *
   * Both used to be state here. `APP-017` moved `Submit a deal` into the masthead, which
   * renders outside `SessionGate` and therefore above this screen, so the state it toggles
   * had to move above both of them. See `src/desk/DeskViewContext.ts`.
   */
  const { tab, goTo, submitOpen, openSubmit, closeSubmit } = useDeskView();

  // Stable identities: `useApiResource` re-reads whenever its loader changes, and an inline
  // arrow would be a new function on every render.
  const dealsLoader = useCallback(() => loadDeals(), [loadDeals]);
  const creditLoader = useCallback(() => loadCredit(), [loadCredit]);
  const auctionLoader = useCallback(() => loadAuction(), [loadAuction]);
  const placementsLoader = useCallback(() => loadPlacements(), [loadPlacements]);
  const bidsLoader = useCallback(() => loadBids(), [loadBids]);

  const deals = useApiResource(dealsLoader);
  const credit = useApiResource(creditLoader);
  const auction = useApiResource(auctionLoader, { refreshMs: AUCTION_REFRESH_MS });
  // History is deliberately not polled and is not re-read after a bid or a purchase. Canon
  // § 16 records a placement when its display day has begun, so this list changes once a
  // day at finalization — and what a founder just bought is tomorrow's, which the auction
  // panel above already reports. Re-reading it here would suggest a bid had a result.
  const placements = useApiResource(placementsLoader);
  // The founder's own ledger of bids (canon APP-010). Not polled: a bid's outcome changes
  // once, at finalization, and the day it changes for is the one the auction panel is
  // already re-reading every minute.
  const bids = useApiResource(bidsLoader);

  // Both panels are re-read from core rather than adjusted locally. The bid response
  // carries the new balance, but the auction's other rows and the number of competing bids
  // have moved too, and `docs/SECURITY.md` § Credits/bids keeps the browser out of the
  // business of deciding what a bid did.
  const auctionRefresh = auction.refresh;
  const creditRefresh = credit.refresh;
  const bidsRefresh = bids.refresh;
  // The bid ledger moves too: a bid just placed is a new standing row, and a bid just raised
  // is the same row at a new amount. Re-read rather than adjusted locally, for the reason
  // above.
  const onBidAccepted = useCallback(() => {
    auctionRefresh();
    creditRefresh();
    bidsRefresh();
  }, [auctionRefresh, creditRefresh, bidsRefresh]);

  // A bought mention moves the same two panels, and for the same reason: the day now holds
  // a listing this founder did not have a moment ago, and the dollar it cost has left their
  // available balance. Re-reading both keeps one screen from stating two balances.
  const onMentionPurchased = useCallback(() => {
    auctionRefresh();
    creditRefresh();
  }, [auctionRefresh, creditRefresh]);

  // An accepted edit can move more than the deal it touched: a material change to an
  // approved deal returns it to review, which takes it off tomorrow's auction and out of
  // the deals that may take an Honorable Mention. Both panels are re-read from core rather
  // than reasoned about here (canon CORE-015: "an edit moves no money", but it does move
  // eligibility, and core is the one that decides it).
  const dealsRefresh = deals.refresh;
  const onDealSaved = useCallback(() => {
    dealsRefresh();
    auctionRefresh();
  }, [dealsRefresh, auctionRefresh]);

  // A submitted deal moves the deals panel and nothing else. Canon § 2 gates bidding on
  // approval, so a deal that has just entered review cannot be on tomorrow's auction, cannot
  // take an Honorable Mention and has moved no credit — re-reading those panels would
  // suggest that filing a deal did something to the money.
  const onDealSubmitted = useCallback(() => {
    dealsRefresh();
  }, [dealsRefresh]);

  const availableCents =
    credit.state.status === "ready" ? credit.state.data.availableCents : null;
  const auctionDate =
    auction.state.status === "ready" ? auction.state.data.displayDate : null;

  // Canon § 10 puts an Honorable Mention behind the same approval gate as every other
  // placement, so the deals that may take one are the approved ones from the deals read —
  // not the auction read's list, which is empty on a day whose auction row does not exist
  // yet even though core would accept a mention for it.
  const approvedDeals =
    deals.state.status === "ready"
      ? deals.state.data.filter((deal) => deal.status === "approved")
      : null;

  // Canon § 7: a deal takes one position a day, so a deal already bidding for the day is
  // told what it holds rather than offered a second listing core would refuse.
  const biddingDealIds =
    auction.state.status === "ready"
      ? auction.state.data.deals.filter((deal) => deal.bid !== null).map((deal) => deal.dealId)
      : [];

  /*
   * The credit panel, rendered into whichever view's rail is showing.
   *
   * One read, two placements. Canon § 5's non-cash terms travel with the figures because
   * `CreditPanel` renders them from `terms.message` verbatim — so the disclosure is present
   * on both views without this screen restating it, which is exactly what `AGENTS.md` § 7
   * requires and what a hand-written second copy would eventually get wrong.
   */
  const creditPanel = (
    <section aria-labelledby="credit-heading" className={desk.railPanel}>
      <SectionHeading
        id="credit-heading"
        level={3}
        title="Your promotional credit"
        description="What you can bid with, what a live bid is holding, and what a win has already spent."
      />
      <AsyncPanel
        state={credit.state}
        reload={credit.reload}
        onSessionEnded={retry}
        loadingLabel="Loading your promotional credit"
        skeleton={<CreditSkeleton />}
        errorTitle="Your credit balance didn't load"
      >
        {(data) => <CreditPanel credit={data} />}
      </AsyncPanel>
    </section>
  );

  return (
    <>
      <DeskNav tab={tab} onSelect={goTo} />
      <Container as="main" id="main" className={styles.main}>
        <div className={desk.identity}>
          {/* No "Seller desk" caption above the name. The masthead's brass surface tag
              already says which of the three Dealbid sites this is, and § Shared component
              patterns keeps it there for exactly that job. */}
          <h1 className={desk.name}>{seller.displayName}</h1>
          <StatusPill
            label={seller.status}
            tone={toneFor(seller.status)}
            description="Account status"
          />
        </div>

        {tab === "deals" ? (
          <div className={desk.layout}>
            <div className={desk.column}>
              <section aria-labelledby="deals-heading">
                <SectionHeading
                  id="deals-heading"
                  title="Your deals"
                  description="Every deal you have submitted, at the review status it holds right now — stated in words, never by colour alone."
                />
                <AsyncPanel
                  state={deals.state}
                  reload={deals.reload}
                  onSessionEnded={retry}
                  loadingLabel="Loading your deals"
                  skeleton={<DealsSkeleton />}
                  errorTitle="Your deals didn't load"
                >
                  {(data) => (
                    <DealsPanel
                      deals={data}
                      loadDealDetail={loadDealDetail}
                      saveDeal={saveDeal}
                      onDealSaved={onDealSaved}
                      loadTerms={loadTerms}
                      submitDeal={submitDeal}
                      onDealSubmitted={onDealSubmitted}
                      submitOpen={submitOpen}
                      onOpenSubmit={openSubmit}
                      onCloseSubmit={closeSubmit}
                    />
                  )}
                </AsyncPanel>
              </section>
            </div>

            <aside className={desk.rail} aria-label="Your credit and tomorrow's auction">
              {creditPanel}
              <section aria-labelledby="auction-state-heading" className={desk.railPanel}>
                <SectionHeading
                  id="auction-state-heading"
                  level={3}
                  meta={
                    auctionDate === null
                      ? "Tomorrow · UTC"
                      : `Tomorrow · ${formatDisplayDateShort(auctionDate)} · UTC`
                  }
                  title="Tomorrow's auction"
                  description="Bidding needs an approved deal. A bid placed today competes for tomorrow's Top 10 — today's is already decided and cannot be bought into. Each day starts at 00:00 UTC."
                />
                <p className={desk.railNote}>
                  Place and raise bids on the{" "}
                  <a
                    href="/auction"
                    onClick={(event) => {
                      event.preventDefault();
                      goTo("auction");
                    }}
                  >
                    Tomorrow&rsquo;s auction
                  </a>{" "}
                  view.
                </p>
              </section>
            </aside>
          </div>
        ) : null}

        {tab === "auction" ? (
          <div className={desk.layout}>
            <div className={desk.column}>
              <section aria-labelledby="auction-heading">
                <SectionHeading
                  id="auction-heading"
                  // The fact in the slot § Shared component patterns keeps for one: which
                  // display day a bid placed here buys, and the timezone that day is named in.
                  meta={
                    auctionDate === null
                      ? "Tomorrow · UTC"
                      : `Tomorrow · ${formatDisplayDateShort(auctionDate)} · UTC`
                  }
                  title="Bid for tomorrow's Top 10"
                  description="Bids placed today compete for tomorrow's board — today's is already decided and cannot be bought into. Each day is independent and opens at a $1 minimum; the higher bid ranks higher, and equal bids favour the earlier one."
                />
                <AsyncPanel
                  state={auction.state}
                  reload={auction.reload}
                  onSessionEnded={retry}
                  loadingLabel="Loading tomorrow's auction"
                  skeleton={<AuctionSkeleton />}
                  errorTitle="Tomorrow's auction didn't load"
                >
                  {(data, receivedAtMs) => (
                    <AuctionPanel
                      view={data}
                      receivedAtMs={receivedAtMs}
                      availableCents={availableCents}
                      placeBid={placeBid}
                      onBidAccepted={onBidAccepted}
                    />
                  )}
                </AsyncPanel>
              </section>

              <section
                aria-labelledby="mentions-heading"
                className={desk.section}
              >
                <SectionHeading
                  id="mentions-heading"
                  // Not the auction section's meta repeated. Canon § 10 sells one display day
                  // rather than a position in a race, so the label states the day it buys
                  // instead of the deadline to beat.
                  meta={
                    auctionDate === null
                      ? "One display day · UTC"
                      : `One display day · ${formatDisplayDateShort(auctionDate)} · UTC`
                  }
                  title="List outside the Top 10"
                  description="An Honorable Mention is a one-day listing in its own section of the board. It is not a Top 10 position and never becomes one."
                />
                <AsyncPanel
                  state={auction.state}
                  reload={auction.reload}
                  onSessionEnded={retry}
                  loadingLabel="Loading Honorable Mentions"
                  skeleton={<MentionsSkeleton />}
                  errorTitle="Honorable Mentions didn't load"
                >
                  {(data) => (
                    <MentionsPanel
                      offer={data.honorableMentions}
                      displayDate={data.displayDate}
                      approvedDeals={approvedDeals}
                      biddingDealIds={biddingDealIds}
                      purchaseMention={purchaseMention}
                      onMentionPurchased={onMentionPurchased}
                    />
                  )}
                </AsyncPanel>
              </section>
            </div>

            <aside className={desk.rail} aria-label="Your credit">
              {creditPanel}
            </aside>
          </div>
        ) : null}

        {tab === "results" ? (
          <div className={desk.column}>
            <section aria-labelledby="history-heading">
              <SectionHeading
                id="history-heading"
                meta="Dates in UTC"
                title="What your placements did"
                description="Every day one of your deals was on the board, why it was there, and the qualified outbound clicks it received."
              />
              <AsyncPanel
                state={placements.state}
                reload={placements.reload}
                onSessionEnded={retry}
                loadingLabel="Loading your placement history"
                skeleton={<HistorySkeleton />}
                errorTitle="Your placement history didn't load"
              >
                {(data) => <HistoryPanel history={data} />}
              </AsyncPanel>
            </section>

            <section aria-labelledby="bids-heading" className={desk.section}>
              <SectionHeading
                id="bids-heading"
                meta="Dates in UTC"
                title="Every bid you have placed"
                // Canon § 14: every bid, every day, won or lost. The sentence says so because
                // the list is now behind a tab, and a founder who has to go looking for it is
                // owed a statement that what they found is all of it.
                description="Every bid since your first, wins and losses alike — nothing sampled and nothing filtered out. What each bid competed for, what became of it, and what happened to the credit it reserved."
              />
              <AsyncPanel
                state={bids.state}
                reload={bids.reload}
                onSessionEnded={retry}
                loadingLabel="Loading every bid you have placed"
                skeleton={<BidsSkeleton />}
                errorTitle="Your bids didn't load"
              >
                {(data) => <BidsPanel history={data} />}
              </AsyncPanel>
            </section>
          </div>
        ) : null}

        {tab === "how-it-works" ? (
          <div className={desk.column}>
            <section aria-labelledby="auction-rules">
              <SectionHeading id="auction-rules" title="How a Dealbid day works" />
              <div className={desk.rules}>
                {AUCTION_RULES.map((rule) => (
                  <Card key={rule.mark} className={desk.rule}>
                    <p className={`${desk.ruleMark} figure`}>{rule.mark}</p>
                    <h3 className={desk.ruleTitle}>{rule.title}</h3>
                    <p className={desk.ruleBody}>{rule.body}</p>
                  </Card>
                ))}
              </div>

              {/*
                * The session note, in the wording this desk already carried.
                *
                * The canvas draws a different one — an email sign-in link, a 30-day session,
                * and a confirmation email a founder can reply to. Canon § 14 and decision
                * `D-008` put seller identity behind Google Sign-In and nothing else, and
                * canon § 18 rules out a delivery engine for V0, so all three describe a
                * product Dealbid does not have. Refusals R2 and R3 in the design record.
                */}
              <div className={desk.session}>
                <p>
                  You are signed in with your Google account, through a session this page cannot
                  read or copy. Nothing in the address bar or in a screenshot of this page would
                  let anyone else reach your desk.
                </p>
                <p>
                  Sign out from the header when you are done on a shared machine. Signing back in
                  takes one click, and Dealbid will never email you a link that opens this desk.
                </p>
              </div>
            </section>
          </div>
        ) : null}
      </Container>
    </>
  );
}
